import { z } from "zod";

import { TRACKER_STATS, type TrackerStat } from "./trackerTrack.js";

// #389: the Progress widget that follows the exercises the athlete picks,
// with the statistics and the range he picks. What he chose is saved on the
// profile (Profile.exerciseTracker); the numbers come from the session
// results (trackerTrack.ts), no endpoint of their own.

/** The range presets, in days. */
export const TRACKER_RANGE_DAYS = { "30d": 30, "12w": 84, "6m": 182, "12m": 365 } as const;
export type TrackerRange = keyof typeof TRACKER_RANGE_DAYS;
export const TRACKER_RANGES = Object.keys(TRACKER_RANGE_DAYS) as TrackerRange[];

export const TRACKER_MAX_EXERCISES = 4;
export const TRACKER_DEFAULT_STATS: readonly TrackerStat[] = ["e1rm", "volume"];
export const TRACKER_DEFAULT_RANGE: TrackerRange = "12w";

/** #392: the free tier, fixed: one exercise, 12 weeks, e1RM. The rest is
 *  premium (entitlements.ts). */
export const TRACKER_FREE_MAX_EXERCISES = 1;
export const TRACKER_FREE_STATS: readonly TrackerStat[] = ["e1rm"];
export const TRACKER_FREE_RANGE: TrackerRange = "12w";

// Plain strings, like widgetLayout.ts: a profile written by a newer build,
// with a stat or a range this build does not know, must still parse. The
// caps are loose for the same reason; resolveExerciseTracker applies ours.
export const ExerciseTrackerSchema = z.object({
  exercises: z.array(z.string().min(1).max(64)).max(20).optional(),
  stats: z.array(z.string().min(1).max(40)).max(20).optional(),
  range: z.string().min(1).max(40).optional(),
});
export type StoredExerciseTracker = z.infer<typeof ExerciseTrackerSchema>;

/** A raw profile value to a stored choice; malformed reads as absent. */
export function parseStoredExerciseTracker(raw: unknown): StoredExerciseTracker | undefined {
  const parsed = ExerciseTrackerSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}

export type ExerciseTrackerChoice = {
  exercises: string[];
  stats: TrackerStat[];
  range: TrackerRange;
};

// Own keys only: «toString» is no range.
const isRange = (s: string): s is TrackerRange => Object.hasOwn(TRACKER_RANGE_DAYS, s);

/** The stored range, or the default when absent or unknown. */
export function resolveTrackerRange(stored: StoredExerciseTracker | undefined): TrackerRange {
  return stored?.range !== undefined && isRange(stored.range) ? stored.range : TRACKER_DEFAULT_RANGE;
}

/** The stored choice, completed. Absent or empty exercises: `fallbackSlug`
 *  (the most logged one in the range). Empty can only be a save made before
 *  there was any history (the last chart has no ✕), so it waits for data.
 *  Absent stats or range: the defaults. Unknown values are dropped,
 *  duplicates too; stats keep the TRACKER_STATS order, so the legend does
 *  not reshuffle. */
export function resolveExerciseTracker(
  stored: StoredExerciseTracker | undefined,
  fallbackSlug: string | null,
): ExerciseTrackerChoice {
  const exercises =
    stored?.exercises === undefined || stored.exercises.length === 0
      ? fallbackSlug === null
        ? []
        : [fallbackSlug]
      : [...new Set(stored.exercises)].slice(0, TRACKER_MAX_EXERCISES);
  const stats =
    stored?.stats === undefined
      ? [...TRACKER_DEFAULT_STATS]
      : TRACKER_STATS.filter((s) => stored.stats?.includes(s));
  return { exercises, stats, range: resolveTrackerRange(stored) };
}

/** #392: the choice as the tier shows it. Premium: unchanged. Free: the
 *  first exercise, the free stat and range. Read side only: the stored
 *  value stays as saved, so a premium account that lapses and comes back
 *  finds its choice. */
export function clampToTier(choice: ExerciseTrackerChoice, premium: boolean): ExerciseTrackerChoice {
  if (premium) return choice;
  return {
    exercises: choice.exercises.slice(0, TRACKER_FREE_MAX_EXERCISES),
    stats: [...TRACKER_FREE_STATS],
    range: TRACKER_FREE_RANGE,
  };
}
